import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import differenceBy from "lodash/differenceBy";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../../../hooks/useCustomizationContext";
import { ReactComponent as IconAddPhoto } from "../../../../../../icons/add-photo.svg";
import { ReactComponent as IconMagic } from "../../../../../../icons/16x16/magic.svg";
import { ReactComponent as IconPlus } from "../../../../../../icons/16x16/plus.svg";
import BlankSelectionProvider from "../../../../../../providers/selection/BlankSelectionProvider";
import { SelectionPhoto } from "../../../../../../types/selection/selection";
import { isDesktopSize } from "../../../../../../utils/customization/device";
import {
  findPageAndDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../../../../utils/customization/finders";
import { findSelectionPhotosInPages } from "../../../../../../utils/customization/page";
import {
  countEditablePicturesInPage,
  getNextLayoutWithMorePictures,
} from "../../../../../../utils/customization/template";
import { orderSelectionByDate } from "../../../../../../utils/selection/list";
import ButtonSmily from "../../../../../atoms/ButtonSmily";
import CheckboxWithLabelSmily from "../../../../../atoms/CheckboxWithLabelSmily";
import TooltipSmily from "../../../../../atoms/TooltipSmily";
import SelectionProviderBrowserPhoto from "../../../../../molecules/selection/SelectionProviderBrowserPhoto";
import { getFrontCoverPage } from "../../../../../../utils/customization/cover";
import { ReactComponent as IconDensity1 } from "./density1.svg";
import { ReactComponent as IconDensity2 } from "./density2.svg";
import { ReactComponent as IconEmptySelection } from "./empty-selection-icons.svg";
import "./style.scss";

type PhotoDensities = 1 | 2;

const DENSITY_STORAGE_NAME = "cheerz_selection_density";
const HIDE_USED_PHOTOS_STORAGE_NAME = "cheerz_hide_used_photos";

const SelectionContent: FunctionComponent<{ elementDefinitionKey?: string; pageKey?: string }> = ({
  elementDefinitionKey,
  pageKey,
}) => {
  const {
    configuration,
    content,
    dispatch,
    selection: { photos: selectionPhotos },
    template,
    ui: { panel },
  } = useCustomizationContext();
  const intl = useIntl();

  const contentPages = content.pages;
  const uploadProvider = configuration ? configuration.uploadProvider : undefined;

  const densityChosen = localStorage.getItem(DENSITY_STORAGE_NAME);
  const hideUsedPhotosAsked = localStorage.getItem(HIDE_USED_PHOTOS_STORAGE_NAME);

  const [hideUsedPhotos, setHideUsedPhotos] = useState<boolean>(hideUsedPhotosAsked === "true");
  const [photosDensity, setPhotosDensity] = useState<PhotoDensities>(densityChosen === "2" ? 2 : 1);
  const [photosDensityAnimate, setPhotosDensityAnimate] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const uploadSelectionProvider = useMemo(() => {
    return configuration?.selectionUploadProvider
      ? configuration.selectionUploadProvider
      : new BlankSelectionProvider();
  }, [configuration]);

  useEffect(() => {
    if (!uploadProvider) {
      return;
    }

    return uploadProvider.onQueueEmpty(() => {
      dispatch({
        code: "KFV-002",
        type: "addNotificationByCode",
      });
    });
  }, [dispatch, uploadProvider, uploadSelectionProvider]);

  const handleClickMorePhotos = () => {
    dispatch({
      open: true,
      type: "setGallery",
    });
  };

  const handleClickHidePhotos = () => {
    setHideUsedPhotos((hidden) => {
      localStorage.setItem(HIDE_USED_PHOTOS_STORAGE_NAME, (!hidden).toString());
      return !hidden;
    });
  };

  const queueSize = uploadProvider?.count();
  const finishedSize = uploadProvider?.count("finished");
  const hasUploadingFiles = !!(queueSize && finishedSize && queueSize > finishedSize);

  const frontCover = getFrontCoverPage(content.pages, template.pageDefinitions);

  const usedSelectionPhotos = findSelectionPhotosInPages(
    contentPages,
    selectionPhotos,
    frontCover?.key || undefined
  );
  const availablePhotos = differenceBy(selectionPhotos, usedSelectionPhotos, "key");

  const handleClickAutofill = () => {
    const orderedSelection = orderSelectionByDate(availablePhotos);

    dispatch({
      configuration,
      selectedPictures: orderedSelection,
      type: "addPicturesWithAutofill",
    });
  };

  const autofillDisabled = availablePhotos.length === 0 || hasUploadingFiles;

  const handleSelect = (selectionPhoto: SelectionPhoto) => {
    if (!pageKey) {
      return;
    }
    dispatch({ type: "setPageToFocus", pageKey });

    const { page, pageDefinition } = findPageAndDefinition(pageKey, template, content);
    const editablePicturesCount = page.editablePictures.length;
    const editablePicturesSlots = countEditablePicturesInPage(pageDefinition);
    const filteredPageDefinitions = findPageDefinitionsWithValidCondition(template, content, {
      type: pageDefinition.type,
      filterType: pageDefinition.filterType,
    });
    const nextLayout = getNextLayoutWithMorePictures(
      pageDefinition.key,
      filteredPageDefinitions,
      editablePicturesCount
    );
    const newPageDefinition =
      editablePicturesSlots - editablePicturesCount > 0 || nextLayout === null
        ? pageDefinition
        : nextLayout;

    if (newPageDefinition.key != pageDefinition.key) {
      dispatch({
        options: configuration?.options,
        pageDefinitionKey: newPageDefinition.key,
        pageKey,
        type: "changePagePageDefinition",
      });
    }

    dispatch({
      configuration,
      editablePictureKey: elementDefinitionKey,
      pageKey,
      selectionPhoto,
      tryToFillFirstEmptyPicture: true,
      type: "addPicture",
    });

    // unselect placeholder to fill other slots
    dispatch({
      type: "setPanel",
      openedTab: panel.openedTab,
      panel: panel.current,
      key: panel.key,
      elementDefinitionKey: undefined,
    });
  };

  const handleClickDensity = (density: PhotoDensities) => {
    setPhotosDensity(density);
    setPhotosDensityAnimate(true);
    window.setTimeout(() => {
      setPhotosDensityAnimate(false);
    }, 500);
    localStorage.setItem(DENSITY_STORAGE_NAME, density.toString());
  };

  const hasPhotosInSelection = selectionPhotos.length > 0;

  const usedPhotos: Set<string> = new Set();
  for (const page of contentPages) {
    if (frontCover && page.key === frontCover.key) {
      continue;
    }

    for (const picture of page.editablePictures) {
      if (!usedPhotos.has(picture.selectionPhotoKey)) {
        usedPhotos.add(picture.selectionPhotoKey);
      }
    }
  }
  const hasOnlyHiddenPhotos =
    hideUsedPhotos && selectionPhotos.length > 0 && selectionPhotos.length === usedPhotos.size;

  return (
    <div className="SelectionContent">
      <div className="SelectionContent-header">
        {intl.formatMessage({ id: "kustom_SelectionContent_title" })}
        {hasPhotosInSelection && (
          <div
            className={classNames("SelectionContent-header-density", {
              "SelectionContent-header-density--animate": photosDensityAnimate,
            })}
          >
            <div
              className={classNames("SelectionContent-header-density-item", {
                "SelectionContent-header-density-item--active": photosDensity === 1,
              })}
              onClick={() => handleClickDensity(1)}
            >
              <IconDensity1 />
            </div>
            <div
              className={classNames("SelectionContent-header-density-item", {
                "SelectionContent-header-density-item--active": photosDensity === 2,
              })}
              onClick={() => handleClickDensity(2)}
            >
              <IconDensity2 />
            </div>
            <span className="SelectionContent-header-density-marker" />
          </div>
        )}
      </div>

      {hasPhotosInSelection && (
        <span className="SelectionContent-action">
          <CheckboxWithLabelSmily
            checked={hideUsedPhotos}
            label={intl.formatMessage({ id: "kustom_SelectionContent_hide_photos" })}
            onChange={handleClickHidePhotos}
            size={isDesktopSize() ? "m" : "s"}
            type="primary"
          />
        </span>
      )}

      <div
        className={classNames("SelectionContent-content", {
          "SelectionContent-content--hiddenPhotos": hasOnlyHiddenPhotos,
        })}
      >
        {hasPhotosInSelection && (
          <div
            className={classNames("SelectionContent-content-main", {
              "SelectionContent-content-main--density2": photosDensity === 2,
            })}
          >
            <button
              className="SelectionContent-content-main-ctaAddPhotos"
              onClick={handleClickMorePhotos}
            >
              <IconAddPhoto />
              <span className="SelectionContent-content-main-ctaAddPhotos-text">
                {intl.formatMessage({ id: "kustom_CharacteristicsSummary_add_photos" })}
              </span>
            </button>

            {selectionPhotos.map((selectionPhoto) => (
              <SelectionProviderBrowserPhoto
                coverPageKey={frontCover?.key || undefined}
                draggable={true}
                hideUsed={hideUsedPhotos}
                key={selectionPhoto.key}
                onSelect={handleSelect}
                pageKey={pageKey}
                selected={false}
                selectionPhoto={selectionPhoto}
                showCount={true}
                uploadProvider={uploadProvider}
              />
            ))}
          </div>
        )}
      </div>

      {(hasPhotosInSelection === false || hasOnlyHiddenPhotos) && (
        <motion.div
          className={classNames("SelectionContent-empty", {
            "SelectionContent-empty--noSelection": !hasPhotosInSelection,
          })}
          layout
        >
          <IconEmptySelection height="72" width="72" />
          {!hasPhotosInSelection && (
            <>
              <p className="SelectionContent-empty-text">
                {intl.formatMessage({ id: "kustom_SelectionContent_selection_empty" })}
              </p>
              <ButtonSmily
                icon={<IconPlus />}
                iconPosition="left"
                onClick={handleClickMorePhotos}
                size="s"
                type="primary"
              >
                <span className="SelectionContent-footer-button-text">
                  {intl.formatMessage({ id: "kustom_SelectionContent_add_photos" })}
                </span>
              </ButtonSmily>
            </>
          )}
          {hasOnlyHiddenPhotos && (
            <p className="SelectionContent-empty-text">
              {intl.formatMessage({ id: "kustom_SelectionContent_selection_hidden" })}
            </p>
          )}
        </motion.div>
      )}

      {hasPhotosInSelection && (
        <div
          className="SelectionContent-footer"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <ButtonSmily
            disabled={autofillDisabled}
            onClick={handleClickAutofill}
            size="xs"
            type="secondary"
          >
            <IconMagic /> {intl.formatMessage({ id: "kustom_SelectionContent_autofill" })}
          </ButtonSmily>
          <AnimatePresence>
            {showTooltip && !autofillDisabled && (
              <motion.span
                animate={{ opacity: 1, y: 0 }}
                className="SelectionContent-footer-animWrapper"
                exit={{ opacity: 0, y: -10, transition: { delay: 0.2 } }}
                initial={{ opacity: 0, y: 10 }}
                transition={{ type: "spring" }}
              >
                <TooltipSmily
                  arrowPosition="downCenter"
                  backgroundColor="tertiaryBlue48"
                  position={{ bottom: "100%", left: "0", right: "0" }}
                >
                  {intl.formatMessage({ id: "kustom_SelectionContent_footer_autofillInfo" })}
                </TooltipSmily>
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default SelectionContent;
